// Los tres interruptores del Chat y el nivel de inteligencia: defaults y persistencia —
// ADR-2026-09-08-el-chat-toma-el-motor-del-asistente.
//
// Todo PURO (el storage se inyecta) para poder testearlo. Vivían dentro del motor propio
// (`useMotorChatAtlas`, borrado); ahora los lee el provider y se los pasa al motor de V2.

import type { Intelligence } from '../pages/v2/agentModels';
import { esMotorDelAgente, type ProveedorDeImagen } from '../lib/proveedoresDeImagen';
import {
  esNivelConfirmacion,
  esPermisoAccion,
  PERMISOS_POR_DEFECTO,
  type PermisosChat,
} from './confirmaciones';
import type { InterruptoresChat } from './tipos';

export const INTERRUPTORES_POR_DEFECTO: InterruptoresChat = {
  // Apagada: los scrapers cuestan créditos de verdad. La prende el usuario.
  investigacion: false,
  busquedaWeb: true,
  respuestasGraficas: true,
};

export const CLAVE_INTERRUPTORES = 'atlas-chat-atlas-interruptores';
export const CLAVE_INTELIGENCIA = 'atlas-chat-atlas-inteligencia';
export const CLAVE_CONFIRMACIONES = 'atlas-chat-atlas-confirmaciones';
/** La tuerca de las cuentas dev: qué proveedor de imagen fuerza el chat
 *  (ADR-2026-09-12-gpt-image-2-5-en-el-harness-y-en-el-agente). */
export const CLAVE_MODELO_DE_IMAGEN = 'atlas-chat-atlas-modelo-de-imagen';
/**
 * "Genial, empecemos": el usuario ya cerró la pantalla de bienvenida
 * (ADR-2026-09-14-la-bienvenida-del-agente). Es un PREFIJO: la clave real lleva el id del
 * usuario pegado (`claveBienvenidaVista`).
 *
 * Va en `localStorage` y no en un `useState` porque la bienvenida tiene que aguantar un
 * REMONTE: el panel lateral y la pantalla dedicada son dos montajes de la misma
 * conversación, así que cerrarla en el panel y expandir a `/agente` la volvía a mostrar —
 * se leía como si el botón no hubiera hecho nada.
 *
 * POR USUARIO y no por navegador (ADR-2026-09-15-la-bienvenida-se-acuerda-por-cuenta): el
 * otro gate —`usuarioTieneChats`— es un hecho de la CUENTA, así que con una sola clave
 * global la primera cuenta que tocaba el botón le comía el saludo a todas las que entraran
 * después en esa compu. Con dos cuentas nuevas en la misma máquina (una demo, un equipo que
 * comparte la notebook) la segunda no veía nunca la bienvenida.
 *
 * Y no reemplaza a `usuarioTieneChats`, la acompaña: esto es lo que ESTE navegador dice que
 * esa cuenta ya vio. Que se limpie al borrar los datos del navegador no rompe nada — vuelve
 * a saludar a alguien que todavía no tiene ningún chat, que es exactamente para quien es el
 * saludo. Por lo mismo no hay migración de la clave vieja y global: a quien la tenía y ya
 * tiene chats no se lo saluda igual.
 */
export const CLAVE_BIENVENIDA_VISTA = 'atlas-chat-atlas-bienvenida-vista';

/** La clave de ESTA cuenta. */
export const claveBienvenidaVista = (usuarioId: string): string =>
  `${CLAVE_BIENVENIDA_VISTA}-${usuarioId}`;

/** Prefijo de las claves de `sessionStorage` del motor para el Chat (mensajes, chat
 *  actual, flag de investigación): las suyas, para no pisar la sesión del asistente si
 *  los dos están montados. Lo lee el provider (para el motor) y el entorno (para
 *  limpiarlas al cambiar de cuenta). */
export const PREFIJO_STORAGE_CHAT = 'atlas-chat-atlas';

/** Lo mínimo de `Storage` que se usa: así el test inyecta un Map. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * Lee los interruptores guardados. Tolerante: lo que falte o venga con otro tipo cae al
 * default, así un valor viejo o corrupto nunca deja el chat sin búsqueda web.
 */
export function leerInterruptores(storage: StorageLike | null | undefined): InterruptoresChat {
  try {
    const crudo = storage?.getItem(CLAVE_INTERRUPTORES);
    if (!crudo) return INTERRUPTORES_POR_DEFECTO;
    const parseado = JSON.parse(crudo) as Partial<Record<keyof InterruptoresChat, unknown>>;
    const bool = (v: unknown, def: boolean): boolean => (typeof v === 'boolean' ? v : def);
    return {
      investigacion: bool(parseado.investigacion, INTERRUPTORES_POR_DEFECTO.investigacion),
      busquedaWeb: bool(parseado.busquedaWeb, INTERRUPTORES_POR_DEFECTO.busquedaWeb),
      respuestasGraficas: bool(
        parseado.respuestasGraficas,
        INTERRUPTORES_POR_DEFECTO.respuestasGraficas,
      ),
    };
  } catch {
    return INTERRUPTORES_POR_DEFECTO;
  }
}

export function guardarInterruptores(
  storage: StorageLike | null | undefined,
  valor: InterruptoresChat,
): void {
  try {
    storage?.setItem(CLAVE_INTERRUPTORES, JSON.stringify(valor));
  } catch {
    /* modo privado: se pierde al cerrar, nada más */
  }
}

/** Nivel de inteligencia guardado. Default `baja` (Gemini Flash, gratis — ADR-608). */
export function leerInteligencia(storage: StorageLike | null | undefined): Intelligence {
  try {
    const v = storage?.getItem(CLAVE_INTELIGENCIA);
    return v === 'baja' || v === 'media' || v === 'alta' ? v : 'baja';
  } catch {
    return 'baja';
  }
}

export function guardarInteligencia(
  storage: StorageLike | null | undefined,
  valor: Intelligence,
): void {
  try {
    storage?.setItem(CLAVE_INTELIGENCIA, valor);
  } catch {
    /* noop */
  }
}

/**
 * El motor de imagen que eligió el usuario en "Opciones de modelo avanzadas", adentro del
 * menú del "+" (ADR-2026-09-14-el-modelo-de-imagen-lo-elige-el-usuario). Default null:
 * Atlas elige.
 *
 * Se valida contra lo que el Agente OFRECE HOY (`esMotorDelAgente`) y no contra el catálogo
 * entero: un id que se dio de baja, el "auto" de alguna versión, o uno que salió de la lista
 * —`openai` salió— cae a null. Si no, quedaba elegido un motor que el selector no puede
 * mostrar y que igual viajaba en cada generación.
 */
export function leerModeloDeImagen(
  storage: StorageLike | null | undefined,
): ProveedorDeImagen | null {
  try {
    const v = storage?.getItem(CLAVE_MODELO_DE_IMAGEN);
    return esMotorDelAgente(v) ? v : null;
  } catch {
    return null;
  }
}

/** null guarda el vacío: no hay `removeItem` en `StorageLike` y el lector lo trata igual. */
export function guardarModeloDeImagen(
  storage: StorageLike | null | undefined,
  valor: ProveedorDeImagen | null,
): void {
  try {
    storage?.setItem(CLAVE_MODELO_DE_IMAGEN, valor ?? '');
  } catch {
    /* noop */
  }
}

/**
 * Los permisos guardados. Default: preguntar todo, que es como funcionó siempre — una
 * preferencia que gasta plata o toca el catálogo NO se hereda de un valor corrupto
 * (ADR-2026-09-08-permisos-tambien-para-el-catalogo).
 *
 * Tolera el formato viejo (un string suelto con el nivel de generación) porque esa clave
 * ya se escribió antes de que los permisos fueran por acción.
 */
export function leerPermisos(storage: StorageLike | null | undefined): PermisosChat {
  try {
    const crudo = storage?.getItem(CLAVE_CONFIRMACIONES);
    if (!crudo) return PERMISOS_POR_DEFECTO;
    // Formato viejo: `"nunca"` a secas (sin comillas JSON tampoco).
    if (esNivelConfirmacion(crudo)) return { ...PERMISOS_POR_DEFECTO, generacion: crudo };
    const p = JSON.parse(crudo) as Partial<Record<keyof PermisosChat, unknown>>;
    return {
      generacion: esNivelConfirmacion(p.generacion)
        ? p.generacion
        : PERMISOS_POR_DEFECTO.generacion,
      productos: esPermisoAccion(p.productos) ? p.productos : PERMISOS_POR_DEFECTO.productos,
      investigacion: esPermisoAccion(p.investigacion)
        ? p.investigacion
        : PERMISOS_POR_DEFECTO.investigacion,
    };
  } catch {
    return PERMISOS_POR_DEFECTO;
  }
}

export function guardarPermisos(
  storage: StorageLike | null | undefined,
  valor: PermisosChat,
): void {
  try {
    storage?.setItem(CLAVE_CONFIRMACIONES, JSON.stringify(valor));
  } catch {
    /* noop */
  }
}

/** ¿Esta CUENTA, en este navegador, ya cerró la pantalla de bienvenida? Ver
 *  `CLAVE_BIENVENIDA_VISTA`. */
export function leerBienvenidaVista(
  storage: StorageLike | null | undefined,
  usuarioId: string,
): boolean {
  try {
    return storage?.getItem(claveBienvenidaVista(usuarioId)) === '1';
  } catch {
    // Sin storage se asume que NO la vio: el saludo es barato y el gate de verdad —tener
    // chats— sigue estando.
    return false;
  }
}

/** Sólo se escribe el sí: "no la vio" es la ausencia, y no hay `removeItem` en
 *  `StorageLike`. Volver a mostrarla no es una acción que el producto ofrezca. */
export function guardarBienvenidaVista(
  storage: StorageLike | null | undefined,
  usuarioId: string,
): void {
  try {
    storage?.setItem(claveBienvenidaVista(usuarioId), '1');
  } catch {
    /* noop */
  }
}
